import { useEffect, useState } from 'react'

/**
 * Make a horizontally scrolling container reachable by keyboard, but only while it
 * actually scrolls.
 *
 * The wide figure grids need `overflow-x: auto`, and a region that scrolls has to be
 * focusable or the content past the right edge is mouse-only (WCAG 2.1.1) - axe reports
 * it as `scrollable-region-focusable`. The obvious fix, adding `tabIndex` to every
 * wrapper, trades one problem for another: containers that fit produce tab stops that
 * announce a region and do nothing. Three of ours were exactly that.
 *
 * Whether a container scrolls is a layout fact, not a render-time one, so it has to be
 * measured - and measured again when anything that affects it changes. Two things do,
 * and the first version of this caught neither:
 *
 *   the ELEMENT arrives late. These wrappers render inside `{data && ...}`, so on the
 *   component's first render the ref is null. An effect with no dependencies runs once,
 *   finds nothing, and never looks again. Hence a callback ref and a dependency on the
 *   node itself.
 *
 *   the CONTENT grows without the container resizing. A table filling with rows changes
 *   scrollWidth, not clientWidth, so a ResizeObserver watching only the container never
 *   fires. It watches the child too.
 *
 * Measured before that second fix: at a 480px viewport the upload history table scrolled
 * and was not focusable - the exact failure this hook exists to prevent.
 */
export function useOverflowFocus(label: string) {
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const [scrollable, setScrollable] = useState(false)

  useEffect(() => {
    if (!node) return

    // +1 absorbs sub-pixel rounding, which otherwise reports a fitting table as
    // scrollable on fractional zoom levels.
    const measure = () => setScrollable(node.scrollWidth > node.clientWidth + 1)
    measure()

    // jsdom has no ResizeObserver, and a unit test rendering one of these pages should
    // not fail over it. A single measurement is the right answer there anyway.
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(measure)
    observer.observe(node)
    if (node.firstElementChild) observer.observe(node.firstElementChild)
    return () => observer.disconnect()
  }, [node])

  return {
    /** Callback ref, so the measurement waits for the element to exist. */
    ref: setNode,
    scrollable,
    /**
     * Spread onto the scrolling element. Empty when it does not scroll, so there is no
     * tab stop and no region to announce.
     *
     * The label is required and should describe the CONTENT: "Scrollable table" names
     * the mechanism, and six identical ones are indistinguishable when navigating by
     * region.
     */
    regionProps: scrollable
      ? ({ tabIndex: 0, role: 'region', 'aria-label': label } as const)
      : ({} as const),
  }
}
